// Component Name:  AddLocation component
// Author: Lavanya S
// Platform: Web
// OS: Windows
// IDE: Visual Studio Code
// Reference: AddProduct
// Date: 11-04-2025
// Description: This component is for both add location and edit location.
//              It allows users to select a start date and an end date, with
//              configurable boundaries and callbacks.
// Dependencies: React,  Redux (for warningToast), API layer to fetch and add both location and image
// Interfaces: Accepts props including selectedLocationId, locationSelected, email
// Maintenance History:  1.0 - First Version

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container, Card, CardBody, Row, Col, Form, FormGroup, Label, Input, Button, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import Modal from "react-modal";
import MarkerImg from "../../assets/images/LK_Icons/LK__Destination Pin A.svg";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { addLocation, getLocationById } from "../../api/locationAPI";
import SearchLocation from "./SearchLocation";
import classnames from "classnames";
import { Link } from "react-router-dom";
import Select from "react-select";
import { createImage, getImage } from "../../api/imageAPI";
import { connect } from "react-redux";
import { locationSelected } from "../../store/locations/actions";
import { warningToast } from "../../store/dbFilter/actions";
import { convertToBase64, formatBase64, inputRequired } from "../Utility/utilsFunctions";
import { addLocationTabValidation } from "../../api/utils/validateAPI";
import UploadImage from "../Utility/UploadImage";
import { amenitiesOptions } from "../Utility/Constants";
import Spinner from "../Utility/Spinner";
import { modalStyle } from "../Utility/styleComponents";
import { ActiveBtn, CancelBtn, InActiveBtn } from "../Utility/Button";
const containerStyle = { width: "100%", height: "75%" };
const libraries = ["places"];
const googleAPIKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

const AddLocation = (props) => {
  const { selectedLocationId, locationSelected, email } = props;
  const breadcrumbItems = [
    { title: "Charger Management System", link: "#" },
    { title: "Add Location", link: "#" },
  ];
  // when modal is opened modal will be the root element
  // on component mount check if location Id is there to fetch the data and prepopulate
  // on unmount remove the location Id from redux
  useEffect(() => {
    Modal.setAppElement("#root");
    if (props.selectedLocationId?.length > 0) {
      fetchLocationData(props.selectedLocationId);
    }

    return () => {
      locationSelected("");
    };
  }, []);

  const [location, setLocation] = useState({
    name: "",
    address: "",
    latitude: "",
    longitude: "",
    hostId: "",
    imageId: "",
    visibility: true,
  });
  const [amenities, setAmenities] = useState([]);

  const [activeTab, setActiveTab] = useState(1);
  // const [isLoaded, setIsLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const center = { lat: 20.5937, lng: 78.9629 };
  const [selectedFiles, setSelectedFiles] = useState([]);
  // console.log("selectedFiles: ", selectedFiles);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const mapRef = useRef();

  const fetchLocationData = async (locationId) => {
    try {
      const data = await getLocationById(locationId);
      const resData = data;
      setLocation({
        name: resData?.name,
        address: resData?.address,
        latitude: resData?.latitude,
        longitude: resData?.longitude,
        hostId: resData?.hostId,
        imageId: resData?.imageId,
        visibility: resData?.visibility,
      });
      setSelectedLocation({ lat: Number(resData?.latitude), lng: Number(resData?.longitude) });
      let localAmenities = [];
      if (resData?.amenities?.length > 0) {
        localAmenities = resData?.amenities.map((item) => ({ value: item, label: item }));
      }
      setAmenities(localAmenities);

      if (resData?.imageId && !resData?.imageId?.startsWith("https://")) {
        const image = await handleGetImage(resData.imageId);
        // console.log("image: ", image.toString());

        setSelectedFiles([
          {
            name: "",
            size: "",
            preview: `${image.toString()}`,
            base64: "", // Base64 string added here
          },
        ]);
      }
    } catch (err) {
      console.log("Error getting the Location", err.msg);
    }
  };

  /**
   * Makes a call to get the Image
   * @param {string} id - to get the Image valid "id"
   * @returns - returns Image data
   */
  const handleGetImage = async (id) => {
    try {
      const response = await getImage(id);
      const { message, data } = response;
      if (!data) return null;
      const imageSrc = formatBase64(data);
      return imageSrc;
    } catch (err) {
      console.log("Error to fetch the image: ", err.msg);
    }
  };

  // refers to the map on google map load
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: googleAPIKey,
    libraries,
  });

  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(center);
      if (selectedLocation) {
        bounds.extend(selectedLocation);
      }
      map.fitBounds(bounds);
    },
    [center, selectedLocation]
  );

  const onUnmount = () => {
    mapRef.current = null;
  };

  // to re-center to the selected location and set the location state to the newly selected address, lat and lng
  const panToLocation = useCallback(({ lat, lng, selectedAddress, isUpdate = false }) => {
    mapRef.current.panTo({ lat, lng });
    setSelectedLocation({ lat, lng });
    if (isUpdate) {
      setLocation((prevState) => {
        return {
          ...prevState,
          address: selectedAddress,
          latitude: lat.toString(),
          longitude: lng.toString(),
        };
      });
    }

    mapRef.current.setZoom(14);
  }, []);

  // validate the inputs for the tab 1
  const validateTab = async () => {
    try {
      const { name, address, latitude, longitude } = location;
      const visibility = location?.visibility && typeof location?.visibility === "string" ? location?.visibility.toLowerCase() : true;
      await addLocationTabValidation.validate({ name, latitude, longitude, address, visibility });
      return true;
    } catch (err) {
      if (err.name === "ValidationError") {
        const msg = "Validation Error: " + err.errors.join(", ");
        props.warningToast({ title: "Validation", message: msg });
        return false;
      }
      props.failureToast({ title: "Error", message: "Unexpected Validation Error" });
    }
  };

  // navigate between the tabs (on validation)
  const handleToggleTab = async (tab) => {
    if (activeTab !== tab) {
      if (tab >= 1 && tab <= 2) {
        if (activeTab === 1 && tab === 2) {
          const isValidated = await validateTab();
          if (isValidated) {
            setActiveTab(tab);
          }
        } else {
          setActiveTab(tab);
        }
      }
    }
  };

  /**
   * Makes a call to create a new location or update the existing one
   * @param {string} ImageId - Id of the image uploaded for this location
   * @returns - returns location data
   */
  const handleCreateLocation = async (imageId) => {
    const locationAmenities = amenities.map((item) => item.value);
    const locationVisibility = location.visibility && typeof location.visibility === "string" ? location.visibility.toLowerCase() : true;
    try {
      const locationDetails = { ...location, visibility: locationVisibility, imageId, amenities: locationAmenities, hostId: email, id: selectedLocationId };
      await addLocation(locationDetails);
      resetLocationDetails();
    } catch (err) {
      console.log("Error creating/updating location: ", err.msg);
    }
  };

  /**
   * Makes a call to create a new Image or update the existing one
   * @returns - returns Image data
   */
  const handleCreateImage = async () => {
    if (selectedFiles[0]?.base64?.length === 0) {
      handleCreateLocation(location?.imageId);
      return;
    }
    console.log("this.state.selectedFiles: ", selectedFiles);
    try {
      const base64 = await convertToBase64(selectedFiles[0]?.base64);
      // console.log("base64: handleCreateImage", base64);
      const imgDetail = { filename: selectedFiles[0]?.name, data: base64 };
      const response = await createImage(imgDetail);
      const { message, data } = response;
      // return data;
      handleCreateLocation(data);
    } catch (err) {
      console.log("Error creating/updating Images: ", err.msg);
    }
  };
  //  to reset the location details
  const resetLocationDetails = () => {
    setLocation({
      name: "",
      address: "",
      latitude: "",
      longitude: "",
      hostId: "",
      imageId: "",
      visibility: true,
    });
    setAmenities([]);
    setSelectedFiles([]);
    setActiveTab(1);
    if (selectedLocationId?.length > 0) {
      props.history.push("/cms-multi-locations");
    }
  };

  // handle the input data to store in the location state
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setLocation({
      ...location,
      [name]: value,
    });
  };

  // Open the modal
  const openModal = () => {
    setShowModal(true);
  };

  // Close the modal and reset the modal related values
  const closeModal = () => {
    setShowModal(false);
    setSelectedLocation(null);
  };

  const handleSelectChange = (selectedOptions) => {
    // If selectedOptions is not null, update the amenities state
    if (selectedOptions && selectedOptions.length > 0) {
      setAmenities(selectedOptions);
    } else {
      setAmenities([]);
    }
  };

  var modalWidth=  props.layoutType === "vertical" ? "80%":"95%";
  var modalHeight =  props.layoutType === "vertical" ? "80%":"85%";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Add Location" breadcrumbItems={breadcrumbItems} />

          <Row>
            {/* Modal to show the map */}
            <Modal
              isOpen={showModal}
              onRequestClose={closeModal}
              contentLabel="Map Modal"
              // onAfterOpen={handleModalOpen} // Trigger resize when modal opens
              style={modalStyle(modalWidth, modalHeight)}
            >
              {/* <LoadScript googleMapsApiKey={googleAPIKey} libraries={libraries} onLoad={handleScriptLoad} onError={handleScriptError}> */}
              {/* search for a particular location and autopopulate the search dropdown with the related suggesstions */}
              <SearchLocation
                panToLocation={panToLocation}
                closeModal={closeModal}
                locationData={{ address: location.address, latitude: location.latitude, longitude: location.longitude }}
              />
              {isLoaded ? (
                <GoogleMap google={props.google} mapContainerStyle={containerStyle} center={selectedLocation || center} zoom={5} onLoad={onMapLoad} onUnmount={onUnmount}>
                  {/* Marker for the selected location  */}
                  {selectedLocation && (
                    <Marker
                      position={selectedLocation}
                      icon={{
                        url: MarkerImg,
                        scaledSize: new window.google.maps.Size(40, 40),
                      }}
                    />
                  )}
                </GoogleMap>
              ) : (
                <div style={styles.loaderContainer}>
                  <Spinner />
                </div>
              )}
              {/* </LoadScript> */}
            </Modal>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="addproduct-nav-pills-wizard" className="twitter-bs-wizard">
                    <Nav pills justified className="twitter-bs-wizard-nav">
                      <NavItem>
                        <NavLink
                          onClick={() => handleToggleTab(1)}
                          className={classnames({
                            active: activeTab === 1,
                          })}
                        >
                          <span className="step-number">01</span>
                          <span className="step-title">Basic Info</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          onClick={() => handleToggleTab(2)}
                          className={classnames({
                            active: activeTab === 2,
                          })}
                        >
                          <span className="step-number">02</span>
                          <span className="step-title">Location Img</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab} className="twitter-bs-wizard-tab-content">
                      <TabPane tabId={1}>
                        <h4 className="card-title">Basic Information</h4>
                        <p className="card-title-desc" style={styles.resetMargin}>
                          Fill all information below
                        </p>

                        {/* Form for Location Details */}
                        <Form className="mt-4">
                          <Row>
                            <Col md={12}>
                              <FormGroup>
                                <Label htmlFor="name">Name{inputRequired()}</Label>
                                <Input id="name" name="name" type="text" className="form-control" value={location.name} onChange={handleFormChange} />
                              </FormGroup>{" "}
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <Label htmlFor="visibility">Visibility</Label>
                                <select id="visibility" name="visibility" className="form-control select2" value={location.visibility} onChange={handleFormChange}>
                                  <option value="true">true</option>
                                  <option value="false">false</option>
                                </select>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="control-label">Amenities</Label>
                                <Select
                                  isMulti
                                  name="features"
                                  options={amenitiesOptions}
                                  className="select2 select2-multiple"
                                  value={amenities} // Pass the formatted selected amenities
                                  onChange={handleSelectChange}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <FormGroup onClick={openModal}>
                                <Label htmlFor="latitude">Latitude{inputRequired()}</Label>
                                <Input id="latitude" name="latitude" type="text" className="form-control" value={location.latitude} onChange={handleFormChange} />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup onClick={openModal}>
                                <Label htmlFor="longitude">Longitude{inputRequired()}</Label>
                                <Input id="longitude" name="longitude" type="text" className="form-control" value={location.longitude} onChange={handleFormChange} />
                              </FormGroup>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={12}>
                              <FormGroup onClick={openModal}>
                                <Label htmlFor="address">Address</Label>
                                <textarea className="form-control" id="address" name="address" rows="3" value={location.address} onChange={handleFormChange}></textarea>
                              </FormGroup>
                            </Col>
                          </Row>
                        </Form>
                      </TabPane>
                      <TabPane tabId={2}>
                        <h4 className="card-title">Location Images</h4>
                        <p className="card-title-desc">Upload Location image</p>
                        <UploadImage selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} />

                        <div className="d-flex justify-content-center mt-4">
                          <ActiveBtn
                            name={location.name ? "Save Location" : "Add Location"}
                            handleClick={() => (selectedFiles?.length === 0 ? handleCreateLocation() : handleCreateImage())}
                          />
                          <CancelBtn name={"Cancel"} handleClick={resetLocationDetails} />
                        </div>
                      </TabPane>
                    </TabContent>
                    <ul className={`pager wizard twitter-bs-wizard-pager-link ${props.layoutType === "vertical" ? "d-flex justify-content-between" : "d-flex flex-column justify-content-center align-items-center"}`}>
                      <li className="mb-2">
                        {activeTab === 1 ? (
                          <InActiveBtn name={"Previous"} />
                        ) : (
                          <ActiveBtn
                            name={"Previous"}
                            handleClick={() => {
                              handleToggleTab(activeTab - 1);
                            }}
                          />
                        )}
                      </li>
                      <li  className="mb-2">
                        {activeTab === 2 ? (
                          <InActiveBtn name={"Next"} />
                        ) : (
                          <ActiveBtn
                            name={"Next"}
                            handleClick={() => {
                              handleToggleTab(activeTab + 1);
                            }}
                          />
                        )}
                      </li>
                    </ul>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

const styles = {
  dropzone: {
    height: "230px",
    width: "100%",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  resetMargin: { margin: "0" },
  openModalHeader: { color: "#005C8E", fontSize: 18, cursor: "pointer" },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  pointer: { cursor: "Pointer" },
  loaderContainer: { height: "250px" },
};

const mapStateToProps = (state) => ({
  email: state.Login.user.email,
  selectedLocationId: state?.LocationManagement?.selectedLocationId,
  layoutType: state.Layout.layoutType,
});

const mapDispatchToProps = (dispatch) => ({
  locationSelected: (payload) => dispatch(locationSelected(payload)),
  warningToast: (payload) => dispatch(warningToast(payload)),
});
export default connect(mapStateToProps, mapDispatchToProps)(AddLocation);
